import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "./Footer";
import "./GetAppShared.css";

import StoreBadges from "../../components/StoreBadges";
import { track, getCommonContext } from "../../utils/track";

import logoIcon from "../../assets/t_red.png";

import shotHome from "../../assets/screenshots/home.png";
import shotDetails from "../../assets/screenshots/details.png";
import shotAdd from "../../assets/screenshots/add.png";
import shotList from "../../assets/screenshots/list.png";
import shotReports from "../../assets/screenshots/reports.png";

function detectDeviceType() {
  const ua = (navigator.userAgent || "").toLowerCase();

  const isAndroid = ua.includes("android");
  const isIOS =
    ua.includes("iphone") ||
    ua.includes("ipad") ||
    ua.includes("ipod") ||
    (ua.includes("mac") && "ontouchend" in document);

  if (isAndroid) return "android";
  if (isIOS) return "ios";
  return "desktop";
}

function isDesktopViewport() {
  return window.matchMedia && window.matchMedia("(min-width: 981px)").matches;
}

const GetAppPage = () => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const navigate = useNavigate();

  // Device logic: ios | android | desktop
  const [deviceType, setDeviceType] = useState(() => detectDeviceType());

  // Viewport logic: desktop layout only when viewport is desktop-sized
  const [desktopViewport, setDesktopViewport] = useState(() => isDesktopViewport());

  // Desktop screenshot selector
  const initialShot = params.get("shot") || "home";
  const [shotKey, setShotKey] = useState(initialShot);

  const shots = {
    home: { label: "Dashboard", img: shotHome, alt: "Toro dashboard screen" },
    details: { label: "Animal details", img: shotDetails, alt: "Toro livestock details screen" },
    add: { label: "Quick add", img: shotAdd, alt: "Toro add livestock screen" },
    list: { label: "List", img: shotList, alt: "Toro livestock list screen" },
    reports: { label: "Reports", img: shotReports, alt: "Toro herd reports screen" },
  };

  const activeShot = shots[shotKey] || shots.home;

  // Keep URL updated so you can link ads like ?shot=details
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("shot", shotKey);
      window.history.replaceState({}, "", url.toString());
    } catch {
      // no-op
    }
  }, [shotKey]);

  // Update device + viewport on resize
  useEffect(() => {
    const onResize = () => setDesktopViewport(isDesktopViewport());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Re-run device detection once on mount (covers edge cases)
  useEffect(() => {
    setDeviceType(detectDeviceType());
  }, []);

  useEffect(() => {
    track(
      "page_view_get_app",
      getCommonContext({
        device_type: deviceType,
        shot: shotKey,
        variant: "organic",
      }),
      { silent: true }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- page view once on mount only
  }, []);

  // Use deviceType for analytics category
  const eventCategory =
    deviceType === "ios"
      ? "get_app_ios"
      : deviceType === "android"
      ? "get_app_android"
      : "get_app_desktop";

  // Desktop-only layout
  if (desktopViewport) {
    return (
      <div className="getapp">
        <header className="getapp-headerDesktop">
          <img
            src={logoIcon}
            alt="Toro"
            className="getapp-logo-img"
            onClick={() => navigate("/")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && navigate("/")}
          />
        </header>

        <main className="getapp-containerDesktop">
          <section className="getapp-desktopHero">
            <div className="getapp-desktopLeft">
              <h1 className="getapp-titleOpt">Track, analyze, and optimize your ranch.</h1>

              <p className="getapp-subtitleOpt">
                Add animals fast, search by VID or EID, and keep weights, breeding, notes, and reports in one place.
              </p>

              {/* On desktop show both stores without the mobile toggle */}
              <div className="getapp-desktopCtas">
                <StoreBadges eventCategory={eventCategory} params={params} showToggleOnMobile={false} />
              </div>

              <ul className="getapp-bulletsOpt getapp-bulletsDesktop">
                <li>Quick adds and structured fields for consistent data</li>
                <li>Instant lookup for any animal record</li>
                <li>Simple herd reporting when you need answers</li>
              </ul>

              <div className="getapp-trustOpt">
                <span className="getapp-pillOpt">Built for ranch workflows</span>
                <span className="getapp-pillOpt">iOS + Android</span>
                <span className="getapp-pillOpt">Secure accounts</span>
              </div>
            </div>

            <div className="getapp-desktopRight">
              <div className="getapp-phoneCard">
                <img className="getapp-heroShot" src={activeShot.img} alt={activeShot.alt} />

                <div className="getapp-shotPickerWrap">
                  <div className="getapp-shotPickerLabel">Preview screens</div>
                  <div className="getapp-shotPicker" role="tablist" aria-label="Choose a screenshot">
                    {Object.entries(shots).map(([key, s]) => (
                      <button
                        key={key}
                        type="button"
                        className={`getapp-shotTab ${shotKey === key ? "isActive" : ""}`}
                        onClick={() => setShotKey(key)}
                        aria-pressed={shotKey === key}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer transparent />
      </div>
    );
  }

  // Mobile install-first layout (Notion/Slack style)
  return (
    <div className="getapp">
      <header className="getapp-headerMinimal">
        <img
          src={logoIcon}
          alt="Toro"
          className="getapp-logo-img"
          onClick={() => navigate("/")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && navigate("/")}
        />
      </header>

      <main className="getapp-containerMobile">
        <section className="getapp-heroMobile">
          <h1 className="getapp-titleMobile">Manage your ranch from anywhere with Toro</h1>

          <p className="getapp-subtitleMobile">
            Add animals fast, search by VID or EID, and keep weights, breeding, and reports in one place.
          </p>

          {/* Keep your platform logic via StoreBadges + your deviceType analytics */}
          <div className="getapp-primaryCta">
            <StoreBadges eventCategory={eventCategory} params={params} showToggleOnMobile={false} />
          </div>

          <div className="getapp-phoneWrap">
            <img src={shotHome} alt="Toro mobile app dashboard" className="getapp-phoneShot" />
          </div>

          <ul className="getapp-bulletsMobile">
            <li>Fast, structured livestock records</li>
            <li>Every animal, full history</li>
            <li>Clear herd insights at a glance</li>
          </ul>
        </section>
      </main>

      <Footer transparent />
    </div>
  );
};

export default GetAppPage;
